import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {NucleotideType} from 'biojs/Util';
import ChemicalStructureSVG from 'chemistry/ChemicalStructureSVG';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {LEGACY_FONT_PROXIMA_12} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {getOptionDataPureTextName, OptionData} from 'ui-library/menus/MenuConstants';
import {isOptionSelectable} from 'ui-library/menus/MenuUtil';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {capitalize, ellipsize} from 'util/NameUtil';

import {MonomerQuery} from './__generated/MonomerQuery';
import {MissingMonomerReason, MONOMER_QUERY, NucleotideComponent} from './MonomerConstants';
import {
  isMonomerNucleotideComponent,
  queryMatchesMonomerName,
  queryMatchesMonomerSymbol,
} from './MonomersUtil';

type Monomer = MonomerQuery['monomers'][number];

export enum SpecialMonomerValue {
  // Keep in sync with benchling.search.interface.filters.monomer_filter
  ANY_MODIFIED = 'ANY_MODIFIED',
  // Keep in sync with benchling.molbio.sequences.modified_nucleotides.modified_nucleotide_ruleset_ops
  ANY_MONOMER = 'ANY_MONOMER',
  ANY_NATURAL_MONOMER = 'ANY_NATURAL_MONOMER',
  EXISTING_MONOMER = 'EXISTING_MONOMER',
}

const SPECIAL_MONOMER_VALUE_NAMES = {
  [SpecialMonomerValue.ANY_MODIFIED]: (nucleotideComponent?: NucleotideComponent) =>
    nucleotideComponent ? `A non-natural ${nucleotideComponent}` : 'A non-natural monomer',
  [SpecialMonomerValue.ANY_MONOMER]: (nucleotideComponent?: NucleotideComponent) =>
    nucleotideComponent ? `Any ${nucleotideComponent} monomer` : 'Any monomer',
  [SpecialMonomerValue.ANY_NATURAL_MONOMER]: (nucleotideComponent?: NucleotideComponent) =>
    nucleotideComponent ? `A natural ${nucleotideComponent}` : 'A natural monomer',
  [SpecialMonomerValue.EXISTING_MONOMER]: () => 'No change',
};

const MONOMER_CHEMICAL_STRUCTURE_IMAGE_MAX_WIDTH = 80;
const MONOMER_CHEMICAL_STRUCTURE_IMAGE_HEIGHT = 40;

function monomerOptionsForSection(
  monomers: Array<Monomer>,
  section: NucleotideComponent,
  selectedMonomerId?: string | MissingMonomerReason,
  nucleotideType?: NucleotideType
): Array<OptionData> {
  return monomers
    .filter(
      (monomer: Monomer) =>
        isMonomerNucleotideComponent(monomer, section, nucleotideType) || monomer.id === selectedMonomerId
    )
    .map((monomer: Monomer) => ({
      name: (
        <div className={css(styles.innerDropdown)}>
          <div>
            <p className={css(styles.name)}>{monomer.name}</p>
            <p className={css(styles.symbol)}>{monomer.symbol}</p>
          </div>
          <ChemicalStructureSVG
            className={css(styles.chemicalStructureContainer)}
            chemicalStructureImage={monomer.chemicalStructureImage}
            svgClassName={css(styles.chemicalStructureSvg)}
          />
        </div>
      ),
      pureTextName: `${monomer.name}`,
      value: monomer.id,
      dataTestAttrs: {component: 'MonomerDropdown', element: 'option', key: monomer.id},
    }));
}

export function buildOptionDatas({
  monomers,
  selectedMonomerId,
  nucleotideComponent,
  nucleotideType,
  filterMonomersPredicate,
  includeNullMonomer,
  allowedSpecialValues,
  allowArchived,
}: {
  monomers: Array<Monomer>;
  selectedMonomerId: string | MissingMonomerReason;
  nucleotideComponent?: NucleotideComponent;
  nucleotideType?: NucleotideType;
  filterMonomersPredicate?: (mon: Monomer) => boolean;
  includeNullMonomer?: boolean;
  allowedSpecialValues?: Array<SpecialMonomerValue>;
  allowArchived?: boolean;
}): Array<OptionData> {
  // eslint-disable-next-line benchlint/no-function-ref-in-array-method
  let filteredMonomers = monomers.filter(filterMonomersPredicate ?? (() => true));
  if (!allowArchived) {
    filteredMonomers = filteredMonomers.filter((mon) => !mon.archiveRecord || mon.id === selectedMonomerId);
  }

  let options: Array<OptionData> = [];

  if (includeNullMonomer) {
    options.push({
      name: 'None',
      pureTextName: 'None',
      value: MissingMonomerReason.NULL_MONOMER,
      dataTestAttrs: {
        component: 'MonomerDropdown',
        element: 'option',
        key: MissingMonomerReason.NULL_MONOMER,
      },
    });
  }
  if (allowedSpecialValues) {
    for (const value of allowedSpecialValues) {
      const name = SPECIAL_MONOMER_VALUE_NAMES[value](nucleotideComponent);
      options.push({
        name,
        pureTextName: name,
        value,
        dataTestAttrs: {component: 'MonomerDropdown', element: 'option', key: value},
      });
    }
  }

  if (nucleotideComponent) {
    return options.concat(
      monomerOptionsForSection(filteredMonomers, nucleotideComponent, selectedMonomerId, nucleotideType)
    );
  }

  for (const section of [
    NucleotideComponent.SUGAR,
    NucleotideComponent.BASE,
    NucleotideComponent.PHOSPHATE,
  ]) {
    options.push({name: capitalize(section), isHeader: true});
    options = options.concat(monomerOptionsForSection(filteredMonomers, section));
  }

  return options;
}

/*
 * Returns optionDatas that match a given query, either by their name or symbol.
 * Argument monomers must represent all monomers in argument optionDatas.
 */
export function getMatchingMonomersByNameOrSymbol(
  query: string,
  optionDatas: Array<OptionData>,
  monomers: Array<Monomer>
): Array<OptionData> {
  const monomerQuery = query.trim();
  if (monomerQuery === '') {
    return optionDatas;
  }
  const monomerIdToSymbol = new Map(monomers.map((m) => [m.id, m.symbol]));
  return optionDatas.filter(
    (optionData) =>
      isOptionSelectable(optionData) &&
      (queryMatchesMonomerSymbol(monomerIdToSymbol.get(optionData.value), monomerQuery) ||
        queryMatchesMonomerName(getOptionDataPureTextName(optionData), monomerQuery))
  );
}

interface MonomerDropdownProps {
  isMenuOpen: boolean;
  nucleotideComponent?: NucleotideComponent;
  nucleotideType?: NucleotideType;
  selectedMonomerId: string | MissingMonomerReason;
  setMenuOpen: (isMenuOpen: boolean) => void;
  updateSelectedMonomerId: (monomerId: string | MissingMonomerReason) => void;
  nameMaxLength?: number;
  filterMonomersPredicate?: (mon: Monomer) => boolean;
  includeNullMonomer?: boolean;
  fullWidth?: boolean;
  allowedSpecialValues?: Array<SpecialMonomerValue>;
  buttonClassName?: string;
  disabled?: boolean;
  allowArchived?: boolean;
}

export default function MonomerDropdown({
  isMenuOpen,
  nucleotideComponent,
  nucleotideType,
  selectedMonomerId,
  setMenuOpen,
  updateSelectedMonomerId,
  nameMaxLength,
  filterMonomersPredicate,
  includeNullMonomer,
  fullWidth,
  allowedSpecialValues,
  buttonClassName,
  disabled,
  allowArchived,
}: MonomerDropdownProps): JSX.Element {
  return (
    <BenchlingQuery<MonomerQuery> query={MONOMER_QUERY} renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}>
      {({monomers}) => {
        const options = buildOptionDatas({
          monomers,
          nucleotideComponent,
          filterMonomersPredicate,
          nucleotideType,
          includeNullMonomer,
          allowedSpecialValues,
          allowArchived,
          selectedMonomerId,
        });
        const optionsByValue: {[id: string]: OptionData} = {};
        for (const option of options) {
          optionsByValue[option.value] = option;
        }
        return (
          <div {...getTestProps({component: 'MonomerDropdown', element: 'root'})}>
            <Dropdown
              isOpen={isMenuOpen}
              menu={
                <SearchableMenu
                  height={Menu.HEIGHTS.MEDIUM}
                  width={Menu.WIDTHS.XLARGE}
                  selectedValue={selectedMonomerId}
                  onSelect={(monomerId) => {
                    if (monomerId !== selectedMonomerId) {
                      updateSelectedMonomerId(monomerId);
                    }
                  }}
                  optionDatas={options}
                  getMatchingOptionDatas={(query, optionDatas) =>
                    getMatchingMonomersByNameOrSymbol(query, optionDatas, monomers)
                  }
                />
              }
              onToggle={
                !disabled
                  ? (isMenuOpen) => {
                      setMenuOpen(isMenuOpen);
                    }
                  : undefined
              }
            >
              <Button
                hideDropdownCaret={false}
                disabled={disabled}
                isDropdown={true}
                preventDropdownFocus={disabled}
                size={ButtonSize.SMALL}
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                fullWidth={fullWidth}
                className={buttonClassName}
                text={
                  selectedMonomerId === MissingMonomerReason.CHOOSING
                    ? 'Select...'
                    : selectedMonomerId === MissingMonomerReason.DEGENERATE
                      ? 'Unknown'
                      : selectedMonomerId === MissingMonomerReason.NULL_MONOMER
                        ? 'None'
                        : ellipsize(optionsByValue[selectedMonomerId].pureTextName!, nameMaxLength ?? 15)
                }
                onClick={() => {
                  setMenuOpen(!isMenuOpen);
                }}
                dataTestAttrs={{
                  component: 'MonomerDropdown',
                  element: 'dropdownButton',
                }}
              />
            </Dropdown>
          </div>
        );
      }}
    </BenchlingQuery>
  );
}

export function SelfOpeningMonomerDropdown(
  props: Omit<MonomerDropdownProps, 'isMenuOpen' | 'setMenuOpen'>
): JSX.Element {
  const [isMenuOpen, setMenuOpen] = useState(false);
  return <MonomerDropdown isMenuOpen={isMenuOpen} setMenuOpen={setMenuOpen} {...props} />;
}

const styles = StyleSheet.create({
  name: {
    marginBottom: 0,
  },
  symbol: {
    ...LEGACY_FONT_PROXIMA_12,
    color: GRAY,
    marginBottom: 0,
  },
  innerDropdown: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: '12px',
  },
  chemicalStructureContainer: {
    height: `${MONOMER_CHEMICAL_STRUCTURE_IMAGE_HEIGHT}px`,
  },
  chemicalStructureSvg: {
    height: '100%',
    maxWidth: `${MONOMER_CHEMICAL_STRUCTURE_IMAGE_MAX_WIDTH}px`,
  },
});
